/** Setup steps: access, language, industry and company. */
import { INDUSTRIES, type Locale } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Select } from "@primereact/ui/select";
import { setLocale } from "../../i18n/index.js";
import { translateError } from "../../lib/form.js";
import { INDUSTRY_DEFAULT_LABELS, type SetupForm, useSetup } from "./wizard.js";

export function CredentialStep() {
  const {
    t,
    credential,
    credentialError,
    validating,
    tokenInput,
    setTokenInput,
    validateSetupToken,
  } = useSetup();
  return (
    <div className="space-y-4">
      <p className="text-sm text-app-muted">
        {t("setup.credential.description")}
      </p>
      {credential?.kind === "invite" ? (
        <div className="flex items-start gap-3 rounded-lg bg-brand-soft px-4 py-3">
          <i className="pi pi-check mt-0.5 shrink-0 text-brand" />
          <div>
            <p className="text-sm font-medium text-app-text">
              {t("setup.credential.inviteDetected")}
            </p>
            <p className="text-sm text-app-muted">
              {t("setup.credential.inviteValid", {
                email: credential.email,
              })}
            </p>
          </div>
        </div>
      ) : credential?.kind === "setup" ? (
        <div className="flex items-start gap-3 rounded-lg bg-brand-soft px-4 py-3">
          <i className="pi pi-check mt-0.5 shrink-0 text-brand" />
          <p className="text-sm font-medium text-app-text">
            {t("setup.credential.title")}
          </p>
        </div>
      ) : (
        <>
          <Field
            label={t("setup.credential.tokenLabel")}
            htmlFor="setup-token"
            hint={t("setup.credential.tokenHint")}
            required
          >
            <div className="flex gap-2">
              <Input
                id="setup-token"
                type="password"
                autoComplete="off"
                value={tokenInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setTokenInput(event.target.value)
                }
              />
              <Button
                type="button"
                variant="outlined"
                severity="secondary"
                loading={validating || undefined}
                onClick={() => void validateSetupToken()}
              >
                {validating
                  ? t("setup.credential.validating")
                  : t("setup.credential.validate")}
              </Button>
            </div>
          </Field>
        </>
      )}
      {credentialError ? (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger"
        >
          <i className="pi pi-exclamation-triangle shrink-0" />
          {credentialError}
        </p>
      ) : null}
    </div>
  );
}

export function LanguageStep() {
  const {
    t,
    setValue,
    values,
    localeChoices,
    uiLocaleChanged,
    documentLocaleChanged,
  } = useSetup();
  return (
    <div className="space-y-3">
      <p className="text-sm text-app-muted">
        {t("setup.language.description")}
      </p>
      <Field label={t("setup.language.title")} htmlFor="setup-language">
        <Select.Root
          id="setup-language"
          value={values.uiLocale}
          onValueChange={(event: { value: unknown }) => {
            const locale = event.value as Locale;
            uiLocaleChanged.current = true;
            setValue("uiLocale", locale, {
              shouldValidate: true,
            });
            setLocale(locale);
            if (!documentLocaleChanged.current) {
              setValue("localization.defaultLocale", locale, {
                shouldValidate: true,
              });
            }
          }}
          fluid
          options={[
            ...localeChoices.map((locale) => ({
              value: locale,
              label: t(`locale.${locale}`),
            })),
          ]}
          optionLabel="label"
          optionValue="value"
        >
          <Select.Trigger type="button" aria-label={t("setup.language.title")}>
            <Select.Value />
            <Select.Indicator className="pi pi-chevron-down" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Positioner>
              <Select.Popup>
                <Select.List />
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
      </Field>
    </div>
  );
}

export function IndustryStep() {
  const { t, setValue, errors, values } = useSetup();
  return (
    <div className="space-y-4">
      <p className="text-sm text-app-muted">
        {t("setup.industry.description", {
          defaultValue:
            "Choose the industry that best matches your company. We will prepare a suitable service catalog.",
        })}
      </p>
      <Field
        label={t("setup.industry.label", {
          defaultValue: "Industry",
        })}
        htmlFor="industry"
        error={translateError(errors.company?.industry?.message, t)}
        required
      >
        <Select.Root
          id="industry"
          invalid={!!errors.company?.industry}
          value={values.company.industry}
          onValueChange={(event: { value: unknown }) =>
            setValue(
              "company.industry",
              event.value as SetupForm["company"]["industry"],
              {
                shouldDirty: true,
                shouldValidate: true,
              },
            )
          }
          fluid
          options={[
            ...INDUSTRIES.map((industry) => ({
              value: industry,
              label: t(`setup.industry.options.${industry}`, {
                defaultValue: INDUSTRY_DEFAULT_LABELS[industry],
              }),
            })),
          ]}
          optionLabel="label"
          optionValue="value"
        >
          <Select.Trigger
            type="button"
            aria-label={t("setup.industry.label", {
              defaultValue: "Industry",
            })}
          >
            <Select.Value />
            <Select.Indicator className="pi pi-chevron-down" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Positioner>
              <Select.Popup>
                <Select.List />
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
      </Field>
    </div>
  );
}

export function CompanyStep() {
  const { t, register, errors } = useSetup();
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field
        label={t("setup.company.name")}
        htmlFor="company-name"
        error={translateError(errors.company?.name?.message, t)}
        required
        className="sm:col-span-2"
      >
        <Input
          id="company-name"
          invalid={!!errors.company?.name}
          {...register("company.name")}
        />
      </Field>
      <Field
        label={t("setup.company.tradingName")}
        htmlFor="trading-name"
        error={translateError(errors.company?.tradingName?.message, t)}
      >
        <Input id="trading-name" {...register("company.tradingName")} />
      </Field>
      <Field
        label={t("setup.company.vatNumber")}
        htmlFor="vat-number"
        error={translateError(errors.company?.vatNumber?.message, t)}
      >
        <Input
          id="vat-number"
          placeholder="CHE-123.456.789"
          {...register("company.vatNumber")}
        />
      </Field>
      <Field
        label={t("setup.company.website")}
        htmlFor="website"
        error={translateError(errors.company?.website?.message, t)}
        className="sm:col-span-2"
      >
        <Input
          id="website"
          type="url"
          inputMode="url"
          placeholder="https://…"
          {...register("company.website")}
        />
      </Field>
    </div>
  );
}
